/**
 * A thread-safe dictionary-like class that provides GetOrAdd functionality
 * similar to .NET's ConcurrentDictionary, ensuring the factory function
 * is called at most once per key.
 */
export class ConcurrentDictionary<TKey, TValue> {
    private readonly map = new Map<TKey, TValue>();
    private readonly pendingKeys = new Set<TKey>();

    /**
     * Gets the value for the specified key, or adds a new value using the factory function
     * if the key doesn't exist. Guarantees the factory is called at most once per key.
     */
    getOrAdd(key: TKey, factory: (key: TKey) => TValue): TValue {
        // First, check if we already have the value
        const existingValue = this.map.get(key);
        if (existingValue !== undefined) {
            return existingValue;
        }

        // Check if another call is already creating this value
        if (this.pendingKeys.has(key)) {
            // Another call is creating this value, wait synchronously
            // Since JS is single-threaded, we need to throw an error
            // or handle this differently. For now, we'll create a new value
            // but this shouldn't happen in practice with proper usage.
            throw new Error(`Concurrent access detected for key: ${key}. This should not happen in single-threaded JavaScript.`);
        }

        // Mark this key as pending
        this.pendingKeys.add(key);

        try {
            // Double-check after marking as pending (in case another call completed)
            const doubleCheckValue = this.map.get(key);
            if (doubleCheckValue !== undefined) {
                return doubleCheckValue;
            }

            // Create the new value
            const newValue = factory(key);
            
            // Store it in the map
            this.map.set(key, newValue);
            
            return newValue;
        } finally {
            // Always remove from pending set
            this.pendingKeys.delete(key);
        }
    }

    /**
     * Gets the value for the specified key, or undefined if not found
     */
    get(key: TKey): TValue | undefined {
        return this.map.get(key);
    }

    /**
     * Sets the value for the specified key
     */
    set(key: TKey, value: TValue): void {
        this.map.set(key, value);
    }

    /**
     * Checks if the dictionary contains the specified key
     */
    has(key: TKey): boolean {
        return this.map.has(key);
    }

    /**
     * Removes the specified key from the dictionary
     */
    delete(key: TKey): boolean {
        this.pendingKeys.delete(key); // Also remove from pending if present
        return this.map.delete(key);
    }

    /**
     * Clears all entries from the dictionary
     */
    clear(): void {
        this.map.clear();
        this.pendingKeys.clear();
    }

    /**
     * Gets the number of entries in the dictionary
     */
    get size(): number {
        return this.map.size;
    }

    /**
     * Returns an iterator for the keys
     */
    keys(): IterableIterator<TKey> {
        return this.map.keys();
    }

    /**
     * Returns an iterator for the values
     */
    values(): IterableIterator<TValue> {
        return this.map.values();
    }

    /**
     * Returns an iterator for the key-value pairs
     */
    entries(): IterableIterator<[TKey, TValue]> {
        return this.map.entries();
    }
}